import { PrismicRichText } from '@prismicio/react';
import { useRef } from 'react';
import {
  Label as LabelComponent,
  Description as DescriptionComponent,
} from 'remote-com-shared/components/CallToAction/Primary';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import { useIntersectionObserver } from 'remote-com-shared/utils/hooks';
import styled, { css } from 'styled-components';

import { transformProperties } from '@/src/domains/prismic/helpers';
import { tabletBreakpoint, desktopBreakpoint } from '@/src/domains/shared/breakpoints';

export const Section = styled.section`
  --brandPrimary: rgba(0, 35, 75, 0.8);

  ${fluidProp(['padding-bottom', 'padding-top'], '80px', '110px')}
  font-family: var(--typography-fontBossa);

  ${ContentContainer} {
    display: flex;
    justify-content: space-around;

    ${desktopBreakpoint(css`
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
    `)}
  }
`;

export const Label = styled(LabelComponent)`
  && {
    color: var(--colors-darkBlue);
    ${({ theme }) => theme.typography.bodySpecial}
    text-transform: uppercase;
    text-align: left;
  }
`;

export const Title = styled.div`
  && {
    color: var(--colors-darkBlue);
    ${({ theme }) => theme.typography.titleSM}
    margin-bottom: 50px;
  }
`;

export const Description = styled(DescriptionComponent)`
  && {
    color: var(--brandPrimary);
    ${({ theme }) => theme.typography.body}
    margin-bottom: 50px;
    font-weight: 400;
  }
`;

const ContentWrapper = styled.div`
  width: 40%;

  ${desktopBreakpoint(css`
    width: 80%;
  `)}
`;

const RatingName = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RatingNumber = styled.div`
  span {
    ${({ theme }) => theme.typography.titleMD}
    font-weight: 600;
    color: var(--brandPrimary);
  }
`;

const RatingLine = styled.div`
  width: 384px;
  height: 19px;
  background: var(--colors-darkBlue);
  border-radius: 5px;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;

  ${tabletBreakpoint(css`
    width: 95%;
  `)}

  &::before {
    content: '';
    height: 19px;
    background: #dfefd3;
    display: block;
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }

  @keyframes widthAnimation {
    0% {
      width: 0;
    }

    100% {
      width: ${({ rating }) => rating * 10}%;
    }
  }
`;

const RatingNote = styled.div`
  ${({ theme }) => theme.typography.bodySM}
  color: var(--brandPrimary);
`;

const RatingTitle = styled(RatingNote)`
  font-weight: 400;
`;

const RatingWrapper = styled.div`
  max-width: 384px;

  ${RatingLine} {
    &::before {
      ${({ $isVisible }) => ($isVisible ? `animation: widthAnimation 5s ease; ` : `opacity: 0;`)};
      animation-fill-mode: both;
    }
  }

  ${({ $wideContent }) => {
    switch ($wideContent) {
      case true:
        return css`
          ${RatingTitle} {
            max-width: 55%;
          }
        `;
      case false:
      default:
        return css``;
    }
  }}
`;

export function RankSlice({ slice }) {
  const { primary } = transformProperties(slice);
  const { label, title, description, percentage } = primary;
  const { fields: ratings } = slice;

  const sectionRef = useRef();
  const isVisible = useIntersectionObserver(sectionRef, {
    threshold: 0,
    rootMargin: '0px',
    unobserve: true,
  });

  return (
    <Section>
      <ContentContainer>
        <ContentWrapper>
          {label && <Label>{label}</Label>}
          {title && (
            <Title as="div">
              <PrismicRichText field={title} />
            </Title>
          )}
          {description && (
            <Description as="div">
              <PrismicRichText field={description} />
            </Description>
          )}
        </ContentWrapper>
        <RatingWrapper $wideContent={percentage} aria-hidden="true" $isVisible={isVisible}>
          {ratings.map(({ title: ratingTitle, rating, note }, index) => (
            <div key={index} ref={sectionRef}>
              {ratingTitle && (
                <RatingName>
                  <RatingTitle>
                    <PrismicRichText field={ratingTitle} />
                  </RatingTitle>
                  {percentage ? (
                    <RatingNumber>
                      <span>{rating * 10}%</span>/100%
                    </RatingNumber>
                  ) : (
                    <RatingNumber>
                      <span>{rating}</span>/10
                    </RatingNumber>
                  )}
                </RatingName>
              )}
              {rating && <RatingLine rating={rating} />}
              {note && (
                <RatingNote>
                  <PrismicRichText field={note} />
                </RatingNote>
              )}
            </div>
          ))}
        </RatingWrapper>
      </ContentContainer>
    </Section>
  );
}
